import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingBag, Clock, Loader, CheckCircle2, XCircle, Wallet,
  PlusCircle, LifeBuoy, ArrowRight, Crown,
} from "lucide-react";
import api, { inr, fmtDate } from "./api";
import { StatCard, StatusBadge, PageHeader } from "./components";
import { Counter, EASE } from "@/components/Motion";

export default function DashboardPage() {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [headline, setHeadline] = useState(null);

  useEffect(() => {
    api.get("/stats/dashboard").then(({ data }) => setStats(data)).catch(() => {});
    // Persisted headline counter: server ticks it +10–100 every minute; we poll and animate.
    const loadCounter = () =>
      api.get("/stats/platform").then(({ data }) => setHeadline(data.counter.value)).catch(() => {});
    loadCounter();
    const iv = setInterval(loadCounter, 60000);
    return () => clearInterval(iv);
  }, []);

  const headlineOrders = headline ?? 420_000_015;

  if (!stats)
    return (
      <div className="flex justify-center py-32">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" data-testid="dashboard-loading" />
      </div>
    );

  const s = stats.by_status;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={
          <>
            Welcome back, <span className="font-serif italic text-gold-grad">{user?.name?.split(" ")[0] || "creator"}.</span>
          </>
        }
        sub="Here's the state of your empire today."
      >
        <Link
          to="/panel/new-order"
          data-testid="quick-new-order-button"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-6 py-3 text-sm font-bold text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] transition-shadow duration-300 hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)]"
        >
          <PlusCircle size={16} /> New Order
        </Link>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard icon={Crown} label="Total Orders" testid="stat-total-orders" delay={0}>
          <span className="text-lg sm:text-xl"><Counter to={headlineOrders} duration={2.4} /></span>
        </StatCard>
        <StatCard icon={Clock} label="Pending" testid="stat-pending" delay={0.06}>
          <Counter to={s.pending || 0} duration={1.6} />
        </StatCard>
        <StatCard icon={Loader} label="In Progress" testid="stat-in-progress" delay={0.12}>
          <Counter to={s.in_progress || 0} duration={1.6} />
        </StatCard>
        <StatCard icon={CheckCircle2} label="Completed" testid="stat-completed" delay={0.18}>
          <Counter to={s.completed || 0} duration={1.6} />
        </StatCard>
        <StatCard icon={XCircle} label="Cancelled" testid="stat-cancelled" delay={0.24}>
          <Counter to={s.cancelled || 0} duration={1.6} />
        </StatCard>
        <StatCard icon={Wallet} label="Account Balance" testid="stat-balance" delay={0.3} accent>
          <Counter to={stats.balance} decimals={2} prefix="₹" duration={1.8} />
        </StatCard>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.45 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6"
          data-testid="recent-orders-card"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-neutral-100">Recent Orders</h3>
            <Link to="/panel/orders" className="flex items-center gap-1 text-xs font-medium text-gold hover:text-goldlight" data-testid="recent-orders-viewall">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <div className="mt-5 space-y-1">
            {stats.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-white/[0.03]">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-gold/80">{o.order_id}</div>
                  <div className="truncate text-sm text-neutral-300">{o.service_name}</div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="text-sm font-medium text-neutral-400">{inr(o.charge)}</span>
                  <StatusBadge status={o.status} />
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.55 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6"
          data-testid="recent-transactions-card"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-neutral-100">Recent Transactions</h3>
            <Link to="/panel/transactions" className="flex items-center gap-1 text-xs font-medium text-gold hover:text-goldlight" data-testid="recent-transactions-viewall">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <div className="mt-5 space-y-1">
            {stats.recent_transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-white/[0.03]">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-neutral-500">{t.txn_id}</div>
                  <div className="truncate text-sm text-neutral-300">{t.note}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className={`text-sm font-semibold ${t.type === "credit" ? "text-emerald-300" : "text-rose-300"}`}>
                    {t.type === "credit" ? "+" : "−"}{inr(t.amount)}
                  </div>
                  <div className="font-mono text-[10px] text-neutral-600">{fmtDate(t.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.section>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
        className="mt-6 grid gap-4 sm:grid-cols-2"
      >
        <Link
          to="/panel/new-order"
          data-testid="quick-action-new-order"
          className="group flex items-center justify-between rounded-2xl border border-gold/25 bg-gradient-to-br from-gold/[0.08] to-transparent p-6 transition-colors duration-300 hover:border-gold/50"
        >
          <div>
            <div className="font-semibold text-neutral-100">Place a new order</div>
            <div className="mt-1 text-sm text-neutral-500">30+ services across 7 platforms</div>
          </div>
          <PlusCircle size={22} className="text-gold transition-transform duration-300 group-hover:rotate-90" />
        </Link>
        <Link
          to="/panel/support"
          data-testid="quick-action-support"
          className="group flex items-center justify-between rounded-2xl border border-white/10 bg-panel/80 p-6 transition-colors duration-300 hover:border-gold/40"
        >
          <div>
            <div className="font-semibold text-neutral-100">Talk to support</div>
            <div className="mt-1 text-sm text-neutral-500">Humans reply in under 5 minutes</div>
          </div>
          <LifeBuoy size={22} className="text-neutral-500 transition-colors group-hover:text-gold" />
        </Link>
      </motion.section>
    </div>
  );
}
